import { useEffect } from 'react'
import type { EnrollmentStep } from '@/features/enrollment'
import { SessionManager } from '../utils/sessionManager'
import { NavigationManager } from '../utils/navigationManager'

/**
 * Custom hook for managing enrollment wizard effects
 */
export const useEnrollmentEffects = (
  sessionId: string | null,
  currentWizardStepIndex: number,
  isCompletedMode: boolean,
  enrollmentDataRef: React.MutableRefObject<any>,
  skipResumeOnceRef: React.MutableRefObject<boolean>,
  movedToPendingRef: React.MutableRefObject<boolean>,
  hiddenTimerRef: React.MutableRefObject<number | null>,
  isRestoringSessionRef: React.MutableRefObject<boolean>,
  setEnrollmentData: (updater: (prev: any) => any) => void,
  setCurrentWizardStepIndex: (index: number) => void,
  setIsCompletedMode: (completed: boolean) => void,
  resetEnrollment: () => void,
  setIsLoading: (loading: boolean) => void,
  steps: EnrollmentStep[]
) => {
  // Initialize managers
  const sessionManager = new SessionManager(
    sessionId,
    () => {}, // setSessionId will be handled by the main hook
    enrollmentDataRef,
    skipResumeOnceRef,
    movedToPendingRef,
    isRestoringSessionRef
  )

  const navigationManager = new NavigationManager(
    sessionManager,
    currentWizardStepIndex,
    setCurrentWizardStepIndex,
    isCompletedMode
  )

  // Handle target step navigation after page reload (for document upload)
  useEffect(() => {
    navigationManager.handleTargetStepNavigation(steps)
  }, [])

  // Check if session exists and is valid on component mount and when sessionId changes
  useEffect(() => {
    // Only resume if we have a sessionId and haven't already resumed
    // ✅ Master Mode optimization: Skip auto-resumption to avoid session collisions and ensure instant loading
    if (!sessionId || skipResumeOnceRef.current || enrollmentDataRef.current?.isMasterMode) {
      setIsLoading(false)
      return
    }

    const init = async () => {
      // Bail out of loading after 8 seconds so a slow backend doesn't block the UI indefinitely
      const loadingTimeout = setTimeout(() => {
        console.warn('⚠️ Session restore timed out — showing fresh form')
        setIsLoading(false)
      }, 8000)

      try {
        await sessionManager.resumeSession(
          setEnrollmentData,
          setCurrentWizardStepIndex,
          setIsCompletedMode,
          resetEnrollment,
          isCompletedMode
        )
      } finally {
        clearTimeout(loadingTimeout)
        setIsLoading(false)
      }
    }
    void init()
  }, [sessionId])

  // Handle page visibility changes and page unload
  useEffect(() => {
    if (!sessionId) return

    const moveToPending = async () => {
      await sessionManager.moveToPending(currentWizardStepIndex)
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Delay moving to pending to avoid immediate bounce when performing quick actions (like link verification)
        if (hiddenTimerRef.current) window.clearTimeout(hiddenTimerRef.current)
        hiddenTimerRef.current = window.setTimeout(() => {
          moveToPending()
        }, 15000) // 15s grace period
      } else {
        if (hiddenTimerRef.current) {
          window.clearTimeout(hiddenTimerRef.current)
          hiddenTimerRef.current = null
        }
      }
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // For synchronous operation during page unload
      if (!movedToPendingRef.current) {
        movedToPendingRef.current = true
        // Fire and forget; best-effort
        sessionManager.moveToPending(currentWizardStepIndex).catch((err) => {
          console.error('Failed to move enrollment session to pending', err)
        })
      }
    }

    const handleFocus = () => {
      // When page gains focus, check if current session is still valid (but not too frequently)
      if (sessionId && !isRestoringSessionRef.current) {
        // Only check validity if we haven't checked recently
        const lastCheck = sessionManager.getLastValidityCheck()
        const now = Date.now()
        if (!lastCheck || (now - lastCheck) > 30000) { // 30 seconds between checks
          sessionManager.checkSessionValidity().then(isValid => {
            if (!isValid) {
              resetEnrollment()
            }
          }).catch(() => {
            resetEnrollment()
          })
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('focus', handleFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('focus', handleFocus)
      if (hiddenTimerRef.current) {
        window.clearTimeout(hiddenTimerRef.current)
        hiddenTimerRef.current = null
      }
    }
  }, [sessionId, currentWizardStepIndex])
}
